import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@material-design-icons/font/index.css';
import '@xyflow/react/dist/style.css';
import './assets/fonts/fonts.css';
import './base-dictionary';
import './containers/notifications/notifications.styles.css';
import './styles/design-tokens.css';
import './styles/index.css';

import { App } from './containers/app/app';
import { appConfig, warnIfRuntimeConfigMissing } from './helpers/app-config/app-config';

warnIfRuntimeConfigMissing();

Sentry.init({
  dsn: appConfig.VITE_APP_SENTRY_DSN,
  tracesSampleRate: 1.0,

  // Sentry v11 removed transaction-based profiling (`profilesSampleRate`).
  // Profiling is now session-based: profileSessionSampleRate is the fraction of
  // sessions that are profiled (1.0 = every session).
  profileSessionSampleRate: 1.0,
  // 'trace' runs the profiler automatically while a sampled root span is active,
  // sending 60s profile chunks. It is the closest v11 equivalent of the old
  // per-transaction profiling, not an identical one. The SDK default is 'manual',
  // which records nothing unless Sentry.uiProfiler.startProfiler() is called.
  profileLifecycle: 'trace',

  environment: appConfig.VITE_APP_ENV_NAME,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.browserProfilingIntegration(),
  ],
});

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
